import { useFormikContext } from "formik";
import { FC, useEffect, useRef } from "react";

import { parseBirthDateFromPesel } from "web/utils/parseBirthDateFromPesel";

import {
  ISubscriptionReceiverFormField,
  ISubscriptionReceiverFormValues,
  SubscriptionActiveFormData,
  SubscriptionActiveFormDataType,
  SubscriptionActiveFormFieldType,
} from "web/types/Subscription";

export interface PeselBirthDatePair {
  peselFieldId: string;
  birthDateFieldId: string;
}

export const getPeselBirthDatePairs = (
  fields: ISubscriptionReceiverFormField[],
  configuration?: SubscriptionActiveFormData[]
): PeselBirthDatePair[] => {
  const peselFields = fields.filter(
    (f) => f.type === SubscriptionActiveFormFieldType.PESEL
  );
  const birthDateFields = fields.filter(
    (f) => f.type === SubscriptionActiveFormFieldType.BIRTHDATE
  );

  const pairs: PeselBirthDatePair[] = [];

  peselFields.forEach((peselField) => {
    birthDateFields.forEach((birthDateField) => {
      const isLinked =
        (!!peselField.requiredWhenFilled &&
          peselField.requiredWhenFilled ===
            birthDateField.requiredWhenFilled) ||
        peselField.requiredWhenFilled === birthDateField.id ||
        birthDateField.requiredWhenFilled === peselField.id;

      if (isLinked) {
        pairs.push({
          peselFieldId: peselField.id,
          birthDateFieldId: birthDateField.id,
        });
      }
    });
  });

  const formSections = configuration ?? [
    {
      id: "default",
      type: SubscriptionActiveFormDataType.FORM,
      fields,
    },
  ];

  formSections.forEach((section) => {
    if (section.type !== SubscriptionActiveFormDataType.FORM) return;

    const sectionPeselFields = section.fields.filter(
      (field) => field.type === SubscriptionActiveFormFieldType.PESEL
    );
    const sectionBirthDateFields = section.fields.filter(
      (field) => field.type === SubscriptionActiveFormFieldType.BIRTHDATE
    );

    if (
      sectionPeselFields.length === 1 &&
      sectionBirthDateFields.length === 1
    ) {
      const inferredPair = {
        peselFieldId: sectionPeselFields[0].id,
        birthDateFieldId: sectionBirthDateFields[0].id,
      };
      const pairAlreadyExists = pairs.some(
        (pair) =>
          pair.peselFieldId === inferredPair.peselFieldId &&
          pair.birthDateFieldId === inferredPair.birthDateFieldId
      );

      if (!pairAlreadyExists) pairs.push(inferredPair);
    }
  });

  return pairs;
};

export const PeselBirthDateAutoFillEffect: FC<{
  pairs: PeselBirthDatePair[];
}> = ({ pairs }) => {
  const { values, setFieldValue } =
    useFormikContext<ISubscriptionReceiverFormValues>();
  const prevPeselValues = useRef<Record<string, string>>({});

  useEffect(() => {
    pairs.forEach(({ peselFieldId, birthDateFieldId }) => {
      const peselValue = (values[peselFieldId] as string) || "";
      const prevPesel = prevPeselValues.current[peselFieldId] ?? "";

      if (peselValue === prevPesel) return;

      prevPeselValues.current[peselFieldId] = peselValue;

      if (peselValue) {
        const birthDateTimestamp = parseBirthDateFromPesel(peselValue);
        if (birthDateTimestamp !== null) {
          setFieldValue(birthDateFieldId, birthDateTimestamp);
        }
      } else {
        setFieldValue(birthDateFieldId, null);
      }
    });
  }, [values, pairs, setFieldValue]);

  return null;
};
